import { createContext, useContext, useEffect, useMemo, useState } from 'react';

const CartContext = createContext(null);

const STORAGE_KEY = 'lumiere_cart_v1';
const GIFT_KEY = 'lumiere_gift_v1';

const loadJSON = (key, fallback) => {
  try {
    if (typeof window === 'undefined') return fallback;
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (e) { return fallback; }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => loadJSON(STORAGE_KEY, []));
  const [selectedGift, setSelectedGift] = useState(() => loadJSON(GIFT_KEY, null));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [giftPickerOpen, setGiftPickerOpen] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch (e) { /* ignore storage full */ }
  }, [items]);

  useEffect(() => {
    try {
      if (selectedGift) localStorage.setItem(GIFT_KEY, JSON.stringify(selectedGift));
      else localStorage.removeItem(GIFT_KEY);
    } catch (e) { /* ignore */ }
  }, [selectedGift]);

  // Auto-open gift picker when cart has items and no gift selected
  useEffect(() => {
    if (drawerOpen && items.length > 0 && !selectedGift) {
      const t = setTimeout(() => setGiftPickerOpen(true), 500);
      return () => clearTimeout(t);
    }
  }, [drawerOpen, items.length, selectedGift]);

  const addItem = ({ product, packSize, quantity = 1 }) => {
    const unit_price = packSize === 'pack' ? product.pack_price : product.single_price;
    const key = `${product.id}_${packSize}`;
    setItems((prev) => {
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) => i.key === key ? { ...i, quantity: i.quantity + quantity } : i);
      }
      return [
        ...prev,
        {
          key,
          product_id: product.id,
          slug: product.slug,
          name: product.name,
          image: product.image,
          accent: product.accent,
          pack_size: packSize,
          pack_count: packSize === 'pack' ? product.pack_count : 1,
          quantity,
          unit_price,
        },
      ];
    });
    setDrawerOpen(true);
  };

  const updateQty = (key, qty) => {
    setItems((prev) => prev.map((i) => i.key === key ? { ...i, quantity: Math.max(1, qty) } : i));
  };
  const removeItem = (key) => setItems((prev) => prev.filter((i) => i.key !== key));
  const clear = () => { setItems([]); setSelectedGift(null); };

  const subtotal = useMemo(() => items.reduce((s, i) => s + i.unit_price * i.quantity, 0), [items]);
  const itemCount = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  // Bundle discount: buy 2+ packs => 10% off pack lines
  const packQty = items.filter((i) => i.pack_size === 'pack').reduce((s, i) => s + i.quantity, 0);
  const packSubtotal = items.filter((i) => i.pack_size === 'pack').reduce((s, i) => s + i.unit_price * i.quantity, 0);
  const bundleDiscount = packQty >= 2 ? +(packSubtotal * 0.1).toFixed(2) : 0;

  const freeShipThreshold = 24;
  const shipping = subtotal - bundleDiscount >= freeShipThreshold || subtotal === 0 ? 0 : 4.99;
  const total = Math.max(0, subtotal - bundleDiscount + shipping);

  const value = {
    items, addItem, updateQty, removeItem, clear,
    drawerOpen, setDrawerOpen,
    subtotal, bundleDiscount, shipping, total, itemCount,
    freeShipThreshold,
    selectedGift, setSelectedGift,
    giftPickerOpen, setGiftPickerOpen,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart outside provider');
  return ctx;
};
